import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
  bgcolor: '#ffffff',
  '&:hover': { boxShadow: 2 },
};

// Same width (md=4) and height for the 6 KPI cards (row 1 + row 2)
const kpiItemSx = { height: '100%', minHeight: 168 };
const kpiCardSx = { ...cardSx, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 168 };
const kpiHeaderSx = { py: 2, px: 3, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 };
const kpiContentSx = { py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 2, flex: 1 };

function AdminLandingPage() {
  const theme = useTheme();
  const { palette } = theme;

  return (
    <>
      {/* KPI row 1: Total Customers, Total Machines, Tools & Tackles – same width/height as row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.primary.main, 0.1), borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <PeopleIcon sx={{ color: 'primary.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                Total Customers
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', fontSize: '2rem' }}>
                1,245
              </Typography>
              <Typography variant="body2" color="text.secondary">
                +12 this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.success.main, 0.1), borderLeft: '4px solid', borderColor: 'success.main' }}>
              <BusinessIcon sx={{ color: 'success.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                Total Machines
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'success.main', fontSize: '2rem' }}>
                3,420
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.secondary.main, 0.1), borderLeft: '4px solid', borderColor: 'secondary.main' }}>
              <BuildIcon sx={{ color: 'secondary.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              Tools And Tackles
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'secondary.main', fontSize: '2rem' }}>
                1,875
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Certificates row: TPI Certificates + Assessment Certificates */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={cardSx}>
            <Box sx={{ bgcolor: alpha(palette.success.main, 0.08), borderLeft: '4px solid', borderColor: 'success.main', py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2, p: 1.25 }}>
                <AssignmentIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              TPI Certificates Issued Count
              </Typography>
            </Box>
            <CardContent sx={{ py: 3.5, px: 3, textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} sx={{ color: 'success.main', fontSize: '2.5rem' }}>
                8,965
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Machines & Tools / Tackles
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={cardSx}>
            <Box sx={{ bgcolor: alpha(palette.primary.main, 0.08), borderLeft: '4px solid', borderColor: 'primary.main', py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 2, p: 1.25 }}>
                <AssessmentIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Operator Assessment Certificates
              </Typography>
            </Box>
            <CardContent sx={{ py: 3.5, px: 3, textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} sx={{ color: 'primary.main', fontSize: '2.5rem' }}>
                2,340
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Inspections completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* KPI row 2: TPI Requests (wider), Access Requests, Worklist Pending */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={5} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.warning.main, 0.1), borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                New TPI Requests
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ fontSize: '2rem' }}>
                42
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.error.main, 0.1), borderLeft: '4px solid', borderColor: 'error.main' }}>
              <LockIcon sx={{ color: 'error.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                New Access Requests
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} color="error.main" sx={{ fontSize: '2rem' }}>
                15
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={kpiItemSx}>
          <Card elevation={0} sx={kpiCardSx}>
            <Box sx={{ ...kpiHeaderSx, bgcolor: alpha(palette.secondary.main, 0.1), borderLeft: '4px solid', borderColor: 'secondary.main' }}>
              <PlaylistAddCheckIcon sx={{ color: 'secondary.main', fontSize: 26 }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              Worklists Pendings
              </Typography>
            </Box>
            <CardContent sx={kpiContentSx}>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'secondary.main', fontSize: '2rem' }}>
                28
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default AdminLandingPage;
